import React, { useEffect, type ReactNode } from 'react';
import { ArrowLeftIcon } from 'lucide-react';
import { Slide } from '../../deck/Slide';
type AppendixSlideProps = {
  eyebrow: string;
  title: string;
  backLabel: string;
  onBack: () => void;
  children: ReactNode;
};

/**
 * A hidden appendix slide. It never appears in the main next / previous flow —
 * it is rendered in place of its parent slide only when the presenter clicks
 * through, and Escape or the back control returns to the parent.
 */
export function AppendixSlide({
  eyebrow,
  title,
  backLabel,
  onBack,
  children
}: AppendixSlideProps) {
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onBack();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onBack]);
  return <Slide data-id="element-17" data-mp="12o67lz4yk1r9">
      <div className="mb-[clamp(0.75rem,1.4vw,1.4rem)] flex flex-shrink-0 items-start justify-between gap-6" data-id="element-18" data-mp="12nxvi9gnt0h4">
        <div data-id="element-19" data-mp="10y87j4t3kdvk">
          <p className="mb-[clamp(0.35rem,0.7vw,0.7rem)] text-[clamp(0.8rem,1.1vw,1.1rem)] font-semibold uppercase tracking-wider text-[var(--brand-green)]" data-id="element-20" data-mp="1125tdy727jj5">
            {eyebrow}
          </p>
          <h2 className="max-w-4xl text-[clamp(1.6rem,3vw,2.75rem)] font-bold leading-[1.08] text-[var(--fg)]" data-id="element-21" data-mp="1aw66onchvv2y">
            {title}
          </h2>
        </div>
        <button type="button" onClick={onBack} className="flex flex-shrink-0 items-center gap-2 rounded-full border border-black/10 bg-[var(--surface)] px-[clamp(0.8rem,1.3vw,1.3rem)] py-[clamp(0.45rem,0.8vw,0.8rem)] text-[clamp(0.78rem,0.98vw,0.98rem)] font-bold uppercase tracking-wider text-[var(--brand-green)] transition-colors hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-green)]" data-id="element-22" data-mp="iamt3z4f6jkt">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-23" data-mp="3qpmxexh0wdcc" />
          {backLabel}
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col" data-id="element-24" data-mp="k8xw6g65idev">{children}</div>
    </Slide>;
}